import { useStore } from "@/store";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { clientInvoicesColumns } from "@/components/tables/clientInvoicesColumns";
import { useMemo } from "react";

interface ClientInvoicesTableProps {
  clientId: number | string;
}

export function ClientInvoicesTable({ clientId }: ClientInvoicesTableProps) {
  const { invoices } = useStore((s) => s);

  // Filter invoices for this client - memoize to prevent unnecessary recalculations
  const clientInvoices = useMemo(() => {
    return invoices.filter((invoice) => invoice.client_id?.toString() === clientId.toString());
  }, [invoices, clientId]);

  // Create the table instance - don't use useMemo here as useReactTable is already a hook
  const table = useReactTable({
    data: clientInvoices,
    columns: clientInvoicesColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Kliento sąskaitos</h2>
      </div>

      {clientInvoices.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          Šis klientas dar neturi sąskaitų
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={clientInvoicesColumns.length} className="h-24 text-center">
                    Sąskaitų nerasta
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
